import { useState, useCallback, useEffect, useRef } from 'react';
import { apiRequest } from '../../utils/apiUtils';
import {
  GridData,
  GridBooking,
  GridVenue,
  GridFixedBooking,
  CreateManualBookingRequest,
  QuickEditBookingRequest,
  VenueBooking,
} from '../../types/venue';

export type GridView = 'daily' | 'weekly';

export interface UseBookingGridOptions {
  initialDate?: string;
  initialView?: GridView;
  venueId?: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export const useBookingGrid = (options: UseBookingGridOptions = {}) => {
  const {
    initialDate = new Date().toISOString().split('T')[0],
    initialView = 'daily',
    venueId,
    autoRefresh = false,
    refreshInterval = 60000, // 1 minute
  } = options;

  const [date, setDate] = useState(initialDate);
  const [view, setView] = useState<GridView>(initialView);
  const [selectedVenueId, setSelectedVenueId] = useState<string | undefined>(venueId);

  const [gridData, setGridData] = useState<GridData>({
    venues: [],
    bookings: [],
    fixedBookings: [],
    dateRange: { start: date, end: date },
  });

  // Keep a stable list of ALL venues (not filtered by selectedVenueId)
  const [allVenues, setAllVenues] = useState<GridVenue[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchGridData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.append('date', date);
    params.append('view', view);
    if (selectedVenueId) params.append('venueId', selectedVenueId);

    const response = await apiRequest<GridData>(`/owner/bookings/grid?${params.toString()}`);

    if (response.data) {
      setGridData(response.data);
      // Only update allVenues when fetching without a venue filter
      if (!selectedVenueId && response.data.venues.length > 0) {
        setAllVenues(response.data.venues);
      }
    } else {
      setError(response.error || 'Error al cargar datos de la grilla');
    }

    setLoading(false);
  }, [date, view, selectedVenueId]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchGridData();
  }, [fetchGridData]);

  // Auto refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchGridData, refreshInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, fetchGridData]);

  // Navigation helpers
  const goToToday = useCallback(() => {
    setDate(new Date().toISOString().split('T')[0]);
  }, []);

  const goToPrevious = useCallback(() => {
    const currentDate = new Date(date);
    if (view === 'weekly') {
      currentDate.setDate(currentDate.getDate() - 7);
    } else {
      currentDate.setDate(currentDate.getDate() - 1);
    }
    setDate(currentDate.toISOString().split('T')[0]);
  }, [date, view]);

  const goToNext = useCallback(() => {
    const currentDate = new Date(date);
    if (view === 'weekly') {
      currentDate.setDate(currentDate.getDate() + 7);
    } else {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    setDate(currentDate.toISOString().split('T')[0]);
  }, [date, view]);

  // Create manual booking
  const createManualBooking = useCallback(
    async (data: CreateManualBookingRequest) => {
      const response = await apiRequest<GridBooking>('/owner/bookings/manual', {
        method: 'POST',
        body: data,
      });

      if (response.data) {
        // Add to bookings list
        setGridData((prev) => ({
          ...prev,
          bookings: [...prev.bookings, response.data!],
        }));
        return { success: true, booking: response.data };
      }

      return { success: false, error: response.error };
    },
    []
  );

  // Quick edit booking
  const quickEditBooking = useCallback(
    async (bookingId: string, data: QuickEditBookingRequest) => {
      const response = await apiRequest<GridBooking>(
        `/owner/bookings/${bookingId}/quick-edit`,
        {
          method: 'PUT',
          body: data,
        }
      );

      if (response.data) {
        // Update booking in list
        setGridData((prev) => ({
          ...prev,
          bookings: prev.bookings.map((b) =>
            b.id === bookingId ? response.data! : b
          ),
        }));
        return { success: true, booking: response.data };
      }

      return { success: false, error: response.error };
    },
    []
  );

  // Cancel booking
  const cancelBooking = useCallback(
    async (bookingId: string) => {
      const response = await apiRequest<VenueBooking>(
        `/bookings/${bookingId}/cancel`,
        { method: 'PUT' }
      );

      if (response.data) {
        // Update booking status in list
        setGridData((prev) => ({
          ...prev,
          bookings: prev.bookings.map((b) =>
            b.id === bookingId
              ? { ...b, status: 'CANCELLED', color: '#FF5252' }
              : b
          ),
        }));
        return { success: true };
      }

      return { success: false, error: response.error };
    },
    []
  );

  // Get booking by slot
  const getBookingAtSlot = useCallback(
    (venueId: string, bookingDate: string, time: string): GridBooking | undefined => {
      return gridData.bookings.find(
        (b) =>
          b.venueId === venueId &&
          b.bookingDate === bookingDate &&
          b.startTime <= time &&
          b.endTime > time &&
          b.status !== 'CANCELLED'
      );
    },
    [gridData.bookings]
  );

  // Check if slot is available
  const isSlotAvailable = useCallback(
    (venueId: string, bookingDate: string, time: string): boolean => {
      return !getBookingAtSlot(venueId, bookingDate, time);
    },
    [getBookingAtSlot]
  );

  return {
    // State
    gridData,
    loading,
    error,
    date,
    view,
    selectedVenueId,

    // Setters
    setDate,
    setView,
    setSelectedVenueId,

    // Actions
    fetchGridData,
    goToToday,
    goToPrevious,
    goToNext,
    createManualBooking,
    quickEditBooking,
    cancelBooking,

    // Helpers
    getBookingAtSlot,
    isSlotAvailable,

    // Derived data
    venues: gridData.venues,
    allVenues,
    bookings: gridData.bookings,
    fixedBookings: gridData.fixedBookings,
    dateRange: gridData.dateRange,
  };
};
